import { uniqBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { gqlQuery } from 'app/lib/graphqlClient'
import type { MetricsRow } from 'app/services/metrics'

export const LOCATION_METRICS_QUERY = graphql(`
  query LocationMetrics(
    $dateRange: DateRangeInput!
    $compareRange: DateRangeInput
    $groupBy: String!
    $channel: String
    $channelOp: String
    $query: String
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $platform: String
    $showDisqualified: Boolean
    $hideMultiLocationPatients: Boolean
  ) {
    marketingMetrics(
      dateRange: $dateRange
      compareRange: $compareRange
      groupBy: $groupBy
      channel: $channel
      channelOp: $channelOp
      query: $query
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      platform: $platform
      showDisqualified: $showDisqualified
      hideMultiLocationPatients: $hideMultiLocationPatients
    ) {
      hits {
        locationId
        bucket
        leads
        MQLs
        mqls
        appointments
        patients
        budget
        production
        scheduledProduction
        ROI
        CPL
        CPQL
        CPA
        CAC
        qualified
        disqualified
        scheduled
        closedWon
        closedLost
        leadsClosedWon
        leadsNeedsAction
        leadsScheduled
        leadsInProgress
        leadsClosedLost
        cpls
        roi
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        patientGoal
        iCPL
        iCPQL
        scheduleRate
      }
      total
      summary {
        leads
        MQLs
        mqls
        appointments
        patients
        budget
        facebookSpend
        googleSpend
        production
        scheduledProduction
        ROI
        CPL
        CPQL
        CPA
        CAC
        qualified
        disqualified
        scheduled
        closedWon
        closedLost
        leadsClosedWon
        leadsNeedsAction
        leadsScheduled
        leadsInProgress
        leadsClosedLost
        leadsByFirstActivityType {
          type
          leads
          mqls
          appointments
          patients
        }
      }
      compareTo {
        leads
        MQLs
        mqls
        appointments
        patients
        budget
        facebookSpend
        googleSpend
        production
        scheduledProduction
        ROI
        CPL
        CPQL
        CPA
        CAC
        qualified
        disqualified
        scheduled
        closedWon
        closedLost
        leadsClosedWon
        leadsNeedsAction
        leadsScheduled
        leadsInProgress
        leadsClosedLost
        leadsByFirstActivityType {
          type
          leads
          mqls
          appointments
          patients
        }
      }
    }
  }
`)

/**
 * Fetches location metrics data from GraphQL based on the provided parameters.
 */
export default function useLocationMetrics(
  fromDate: string,
  toDate: string,
  compareFromDate: string,
  compareToDate: string,
  locationIds: string[],
  channel: string,
  channelOp: string | null,
  query: string,
  integrationStatus?: string,
  filters?: string,
  platform?: string | null,
  showDisqualified?: boolean,
  hideMultiLocationPatients?: boolean,
): [number, MetricsRow | undefined, MetricsRow[] | undefined, MetricsRow | undefined, boolean] {
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<MetricsRow>()
  const [locationRows, setLocationRows] = useState<MetricsRow[]>()
  const [compareTo, setCompareTo] = useState<MetricsRow>()
  const [isLoading, setIsLoading] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!fromDate || !toDate || !compareFromDate || !compareToDate) return
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    const dateRange = { fromDate, toDate }
    const compareRange = { fromDate: compareFromDate, toDate: compareToDate }
    const vars: Record<string, unknown> = { dateRange, compareRange, groupBy: `locations` }
    if (locationIds.length) vars.locationIds = locationIds
    if (channel) vars.channel = channel
    if (channelOp) vars.channelOp = channelOp
    if (query) vars.query = query
    if (integrationStatus) vars.integrationStatus = integrationStatus
    if (filters) vars.filters = filters
    if (platform) vars.platform = platform
    if (showDisqualified !== undefined) vars.showDisqualified = showDisqualified
    if (hideMultiLocationPatients !== undefined) vars.hideMultiLocationPatients = hideMultiLocationPatients
    setIsLoading(true)
    gqlQuery<{ marketingMetrics: { hits: MetricsRow[]; total: number; summary: MetricsRow; compareTo: MetricsRow } }>(
      LOCATION_METRICS_QUERY,
      vars,
    )
      .then((result) => {
        if (requestId !== requestIdRef.current || !result.data) return
        const body = result.data.marketingMetrics
        setLocationRows(uniqBy(body.hits, (i) => i.locationId))
        setTotal(body.total)
        setSummary(body.summary)
        setCompareTo(body.compareTo)
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false)
      })
  }, [
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    locationIds,
    channel,
    channelOp,
    query,
    integrationStatus,
    filters,
    platform,
    showDisqualified,
    hideMultiLocationPatients,
  ])

  return [total, summary, locationRows, compareTo, isLoading]
}
